
// client/src/components/event/EventActionsList.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaHome, FaArrowLeft } from "react-icons/fa";
import AppFooter from "../AppFooter";

const ACTION_META = {
  auction:         { label: "Auction",           icon: "bi-hammer",             color: "#ff6800" },
  raffle:          { label: "Raffle",            icon: "bi-ticket-perforated",  color: "#6b8cff" },
  challenge:       { label: "Challenge",         icon: "bi-trophy",             color: "#ffc107" },
  contest:         { label: "Contest",           icon: "bi-award",              color: "#ce93d8" },
  ama:             { label: "AMA",               icon: "bi-chat-dots",          color: "#42a5f5" },
  ugc_campaign:    { label: "UGC Campaign",      icon: "bi-camera",             color: "#4caf50" },
  themed_day:      { label: "Themed Day",        icon: "bi-hash",               color: "#ff7043" },
  collab_takeover: { label: "Collab / Takeover", icon: "bi-people",             color: "#26c6da" },
  poll_quiz:       { label: "Poll / Quiz",       icon: "bi-bar-chart",          color: "#ab47bc" },
  default:         { label: "Action",            icon: "bi-lightning",           color: "#8899b0" },
};

const EventActionsList = () => {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");

  const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await axios.get(`${baseUrl}/api/events/${tenant}/event-actions`);
        if (resp.data.success) setActions(resp.data.actions || []);
      } catch (err) {
        console.error("Error loading event actions:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenant, baseUrl]);

  const actionTypes = useMemo(() => {
    const types = new Set(actions.map(a => a.action));
    return Array.from(types).sort();
  }, [actions]);

  const filtered = useMemo(() => {
    let items = [...actions];
    if (filterType !== "all") {
      items = items.filter(a => a.action === filterType);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(a =>
        (a.item_name || "").toLowerCase().includes(q) ||
        (a.event_name || "").toLowerCase().includes(q) ||
        (a.action || "").toLowerCase().includes(q)
      );
    }
    return items;
  }, [actions, filterType, searchQuery]);

  const getMeta = (type) => ACTION_META[type] || ACTION_META.default;

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const handleClick = (action) => {
    navigate(`/${tenant}/${action.eventid}/${action.itemid}/actionDetail?type=${action.action}`);
  };

  return (
    <div className="dark-inline-surface" style={{ padding: "20px", paddingTop: 140, paddingBottom: 80, maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: "#fff", fontSize: "1.35rem", fontWeight: 700 }}>Event Actions</h2>
        <span style={{ color: "rgba(255,255,255,0.78)", fontSize: 13 }}>{filtered.length} action{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/dashboard`)}>
          <FaHome /> Home
        </button>
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="Search by name, event, or type..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            flex: 1, minWidth: 200, padding: "8px 12px",
            background: "#0f0f1a", border: "1px solid #2a2a50", borderRadius: 6,
            color: "#e8edf5", fontSize: 13,
          }}
        />
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          style={{
            padding: "8px 12px", background: "#0f0f1a", border: "1px solid #2a2a50",
            borderRadius: 6, color: "#e8edf5", fontSize: 13,
          }}
        >
          <option value="all">All Types</option>
          {actionTypes.map(t => (
            <option key={t} value={t}>{getMeta(t).label}</option>
          ))}
        </select>
      </div>

      {loading && <div style={{ textAlign: "center", padding: 48, color: "rgba(255,255,255,0.78)" }}>Loading actions...</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: 48, color: "#556" }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>
            <i className="bi bi-lightning" />
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.78)" }}>
            {actions.length === 0 ? "No event actions created yet" : "No actions match your filters"}
          </div>
        </div>
      )}

      {/* Actions List */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered.map(action => {
          const meta = getMeta(action.action);
          return (
            <div
              key={action.id}
              onClick={() => handleClick(action)}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "14px 16px", background: "#16213e",
                borderRadius: 10, cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.06)",
                transition: "background 0.15s, border-color 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,104,0,0.08)"; e.currentTarget.style.borderColor = "rgba(255,104,0,0.2)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#16213e"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; }}
            >
              {/* Icon */}
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: `${meta.color}20`, color: meta.color,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, flexShrink: 0,
              }}>
                <i className={`bi ${meta.icon}`} />
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#e8edf5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {action.item_name || "Unnamed Item"}
                  </span>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 10,
                    fontWeight: 700, textTransform: "uppercase",
                    background: `${meta.color}20`, color: meta.color,
                  }}>
                    {meta.label}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.78)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {action.event_name || "Event"} {action.startdate ? `\u00B7 ${formatDate(action.startdate)}` : ""}
                </div>
              </div>

              {/* Arrow */}
              <div style={{ color: "#556", fontSize: 16, flexShrink: 0 }}>
                <i className="bi bi-chevron-right" />
              </div>
            </div>
          );
        })}
      </div>

      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${tenant}/dashboard`),
          },
        ]}
      />
    </div>
  );
};

export default EventActionsList;
